import { useState, useRef, useEffect } from 'react';

interface ImageUploaderProps {
    label: string;
    value?: File | string;
    onChange: (file: File | null) => void;
    required?: boolean;
    aspectRatio?: string;
    maxSize?: number; // in MB
}

/**
 * ImageUploader component - Upload image with preview
 */
export default function ImageUploader({
    label,
    value,
    onChange,
    required = false,
    aspectRatio = '16:9',
    maxSize = 5,
}: ImageUploaderProps) {
    const [preview, setPreview] = useState<string | null>(null);
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update preview when value prop changes (for edit mode with existing images)
    useEffect(() => {
        if (typeof value === 'string') {
            setPreview(value);
        } else if (value instanceof File) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result as string);
            };
            reader.readAsDataURL(value);
        } else {
            setPreview(null);
        }
    }, [value]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file');
            return;
        }

        // Validate file size
        if (file.size > maxSize * 1024 * 1024) {
            setError(`File size must be less than ${maxSize}MB`);
            return;
        }

        setError('');
        onChange(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleRemove = () => {
        onChange(null);
        setPreview(null);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-[#1e293b]">
                {label}
                {required && <span className="text-[#ef4444] ml-1">*</span>}
            </label>

            {preview ? (
                <div className="relative group max-w-sm">
                    <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-40 object-cover rounded-lg border-2 border-[#e2e8f0]"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={handleClick}
                            className="px-4 py-2 bg-white text-[#1e293b] rounded-lg hover:bg-[#f8fafc] transition-colors font-medium text-sm"
                        >
                            Change
                        </button>
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="px-4 py-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors font-medium text-sm"
                        >
                            Remove
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={handleClick}
                    className="w-full max-w-sm h-40 border-2 border-dashed border-[#cbd5e1] rounded-lg hover:border-[#6e64f0] hover:bg-[#f5f3ff] transition-all flex flex-col items-center justify-center gap-2 group"
                >
                    <svg
                        className="w-10 h-10 text-[#cbd5e1] group-hover:text-[#6e64f0] transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    <p className="text-sm text-[#64748b] group-hover:text-[#6e64f0] font-medium">
                        Click to upload image
                    </p>
                    <p className="text-xs text-[#94a3b8]">
                        {aspectRatio} • Max {maxSize}MB
                    </p>
                </button>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
            />

            {error && <p className="text-xs text-[#ef4444]">{error}</p>}
        </div>
    );
}
